import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import IconAdd from '../../components/icons/IconAdd';
import IconDelete from '../../components/icons/IconDelete';
import IconSearch from '../../components/icons/IconSearch';
import IconSudent from '../../components/icons/IconSudent';
import CourseCreateModal from './CourseCreateModal';
import { LEVELS_BY_KEY } from './courseLevels';
import * as api from './api';
import language from '../../variables/language';
import '../../css/courses.css';

const courseLevel = LEVELS_BY_KEY.course;

// A Topic's course is only ever reachable through whichever of its 5
// parent fields is set - each parent is EAGER-loaded all the way up to
// its own `course`, so no separate id->course lookup table is needed,
// just walking the one populated branch.
function resolveCourseIdFromTopic(topic) {
   if(topic.course) return topic.course.id;
   if(topic.module) return topic.module.course.id;
   if(topic.chapter) return topic.chapter.module.course.id;
   if(topic.unit) return topic.unit.chapter.module.course.id;
   if(topic.lesson) return topic.lesson.unit.chapter.module.course.id;
   return null;
}

// TopicElement doesn't have separate image/audio/video types - every media
// block shares one type ("media"), and the actual kind lives inside its
// JSON config (config.kind: "image"|"audio"|"video"), same shape
// MediaBlockEditor.jsx writes.
function mediaKind(topicElement) {
   if(topicElement.type !== 'media' || !topicElement.config) return null;
   try {
      const parsed = JSON.parse(topicElement.config);
      return parsed && parsed.kind ? parsed.kind : null;
   } catch(e) {
      return null;
   }
}

const Courses = () => {
   const navigate = useNavigate();
   const lang = language[language.lang];
   const [courses, setCourses] = useState([]);
   const [search, setSearch] = useState('');
   const [refresh, setRefresh] = useState(0);
   const [createOpen, setCreateOpen] = useState(false);
   const [deletingId, setDeletingId] = useState(null);

   useEffect(() => {
      Promise.all([
         api.getList(courseLevel.apiBase),
         api.getList('/api/topics'),
         api.getList('/api/topicexercisetemplates'),
         api.getList('/api/topicelements'),
         api.getList('/api/studentcourses').catch(() => ({ data: [] })),
      ]).then(([coursesRes, topicsRes, exercisesRes, elementsRes, studentCoursesRes]) => {
         const coursesData = Array.isArray(coursesRes.data) ? coursesRes.data : [];
         const topics = Array.isArray(topicsRes.data) ? topicsRes.data : [];
         const topicExercises = Array.isArray(exercisesRes.data) ? exercisesRes.data : [];
         const topicElements = Array.isArray(elementsRes.data) ? elementsRes.data : [];
         const studentCourses = Array.isArray(studentCoursesRes.data) ? studentCoursesRes.data : [];

         // topic id -> course id, built once and reused so tallying the
         // (much larger) topicexercisetemplate/topicelement lists is a
         // plain lookup instead of re-walking the ancestor chain per row.
         const topicCourseId = {};
         const topicCountByCourse = {};
         topics.forEach(topic => {
            const courseId = resolveCourseIdFromTopic(topic);
            if(courseId == null) return;
            topicCourseId[topic.id] = courseId;
            topicCountByCourse[courseId] = (topicCountByCourse[courseId] || 0) + 1;
         });

         const exerciseCountByCourse = {};
         topicExercises.forEach(row => {
            const topicId = row.topic && row.topic.id;
            const courseId = topicId != null ? topicCourseId[topicId] : null;
            if(courseId == null) return;
            exerciseCountByCourse[courseId] = (exerciseCountByCourse[courseId] || 0) + 1;
         });

         const audioCountByCourse = {};
         const videoCountByCourse = {};
         topicElements.forEach(row => {
            const kind = mediaKind(row);
            if(kind !== 'audio' && kind !== 'video') return;
            const topicId = row.topic && row.topic.id;
            const courseId = topicId != null ? topicCourseId[topicId] : null;
            if(courseId == null) return;
            const bucket = kind === 'audio' ? audioCountByCourse : videoCountByCourse;
            bucket[courseId] = (bucket[courseId] || 0) + 1;
         });

         // A course's "students" are only the rows whose `course` (not
         // `sourceCourse`) points directly at it - a student is actually
         // enrolled in whatever Course row studentcourse.course_id names,
         // full stop. A copy's source template is explicitly NOT counted as
         // "having" that student - the student uses their own independent
         // copy, not the template it was made from (per explicit user
         // correction: the badge must reflect course_id only).
         const studentCountByCourse = {};
         studentCourses.forEach(row => {
            const directId = row.course && row.course.id;
            if(directId != null) studentCountByCourse[directId] = (studentCountByCourse[directId] || 0) + 1;
         });

         setCourses(coursesData.map(c => ({
            ...c,
            topicCount: topicCountByCourse[c.id] || 0,
            exerciseCount: exerciseCountByCourse[c.id] || 0,
            audioCount: audioCountByCourse[c.id] || 0,
            videoCount: videoCountByCourse[c.id] || 0,
            studentCount: studentCountByCourse[c.id] || 0,
         })));
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
      });
   }, [refresh, navigate]);

   const visibleCourses = useMemo(() => {
      const query = search.trim().toLowerCase();
      const filtered = query
         ? courses.filter(c => ((c.name || '') + ' ' + (c.description || '')).toLowerCase().includes(query))
         : courses;
      return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
   }, [courses, search]);

   const totals = useMemo(() => ({
      topics: courses.reduce((sum, c) => sum + c.topicCount, 0),
      exercises: courses.reduce((sum, c) => sum + c.exerciseCount, 0),
      audio: courses.reduce((sum, c) => sum + c.audioCount, 0),
      video: courses.reduce((sum, c) => sum + c.videoCount, 0),
   }), [courses]);

   const refreshList = () => setRefresh(prev => prev + 1);

   const openCreate = () => setCreateOpen(true);
   const closeCreate = () => setCreateOpen(false);
   const onCreated = (createdCourse) => {
      closeCreate();
      if(createdCourse && createdCourse.id) {
         navigate('/courses/' + createdCourse.id);
      } else {
         refreshList();
      }
   };

   const handleDelete = (e, course) => {
      e.stopPropagation();
      if(deletingId) return;
      setDeletingId(course.id);
      api.hasChildren(courseLevel.childChecks[0], course.id).then(blocked => {
         if(blocked) {
            window.alert('Неможливо видалити курс «' + course.name + '»: спочатку видаліть усі його модулі.');
            setDeletingId(null);
            return;
         }
         if(!window.confirm('Видалити курс «' + course.name + '»?')) {
            setDeletingId(null);
            return;
         }
         api.deleteOne(courseLevel.apiBase, course.id)
            .then(() => refreshList())
            .catch(() => window.alert('Не вдалося видалити курс - можливо він досі повʼязаний з іншими елементами.'))
            .finally(() => setDeletingId(null));
      }).catch(() => setDeletingId(null));
   };

   return (
      <Layout>
         <PageHeader name='Курси'>
            <div className='courses-header-stats'>
               <span className='courses-header-stat-pill'>
                  <b>{courses.length}</b>
                  <small>курсів</small>
               </span>
               <span className='courses-header-stat-pill'>
                  <b>{totals.topics}</b>
                  <small>тем</small>
               </span>
               <span className='courses-header-stat-pill'>
                  <b>{totals.exercises}</b>
                  <small>вправ</small>
               </span>
               <span className='courses-header-stat-pill'>
                  <b>{totals.audio}</b>
                  <small>аудіо</small>
               </span>
               <span className='courses-header-stat-pill'>
                  <b>{totals.video}</b>
                  <small>відео</small>
               </span>
            </div>
            <div className='courses-header-actions'>
               <div className='courses-add-btn' onClick={openCreate} title='Створити курс'>
                  <IconAdd />
               </div>
            </div>
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c courses-page'>

               <div className='courses-search-bar'>
                  <IconSearch />
                  <input className='courses-search-input' placeholder='Пошук курсу за назвою або описом...'
                     value={search} onChange={e => setSearch(e.target.value)} />
                  {search ? (
                     <div className='courses-search-clear' onClick={() => setSearch('')}>×</div>
                  ) : ''}
                  <span className='courses-search-count'>Знайдено: {visibleCourses.length}</span>
               </div>

               {visibleCourses.length === 0 ? (
                  <div className='courses-empty'>{lang['Empty list']}</div>
               ) : (
                  <div className='courses-grid'>
                     {visibleCourses.map(course => (
                        <div key={course.id} className='course-card' onClick={() => navigate('/courses/' + course.id)}>
                           {course.studentCount > 0 ? (
                              <span className='course-card-students-badge' title='Студентів використовують цей курс'>
                                 <IconSudent style={{ width: 14, height: 14 }} />
                                 {course.studentCount}
                              </span>
                           ) : ''}
                           <div className='course-card-body'>
                              <div className='course-card-name'>{course.name}</div>
                              {course.description ? (
                                 <div className='course-card-desc'>{course.description}</div>
                              ) : (
                                 <div className='course-card-desc course-card-desc-empty'>Без опису</div>
                              )}
                           </div>
                           <div className='course-card-footer'>
                              <div className='course-card-stats'>
                                 <span className='course-card-stat-pill' title='Тем у курсі'>
                                    <span className='course-card-stat-value'>{course.topicCount}</span> тем
                                 </span>
                                 <span className='course-card-stat-pill' title='Підʼєднаних вправ'>
                                    <span className='course-card-stat-value'>{course.exerciseCount}</span> вправ
                                 </span>
                                 <span className='course-card-stat-pill course-card-stat-pill-audio' title='Аудіо у контенті курсу'>
                                    <span className='course-card-stat-value'>{course.audioCount}</span> аудіо
                                 </span>
                                 <span className='course-card-stat-pill course-card-stat-pill-video' title='Відео у контенті курсу'>
                                    <span className='course-card-stat-value'>{course.videoCount}</span> відео
                                 </span>
                              </div>
                              <div className='course-card-actions' onClick={e => e.stopPropagation()}>
                                 <div className='button-icon' onClick={(e) => handleDelete(e, course)}>
                                    <IconDelete style={{ fill: '#e0876c', width: 16, height: 16 }} />
                                 </div>
                              </div>
                           </div>
                        </div>
                     ))}
                  </div>
               )}
            </div>
         </main>
         <CourseCreateModal isOpen={createOpen} onClose={closeCreate} onCreated={onCreated} />
      </Layout>
   );
};

export default Courses;
